import type { CellContext } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { ForecastPaginatedTableEditableCell } from 'src/features/forecastPaginated/forecastPaginatedTable/ForecastPaginatedTableEditableCell';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

export type TableDataRow = {
  metric: string;
  color: string;
  values: (string | number)[];
};

export const isEditableMonth = (year: number, month: number): boolean => {
  const now = new Date();
  const [currentYear, currentMonth] = [now.getFullYear(), now.getMonth() + 1];
  return year > currentYear || (year === currentYear && month >= currentMonth);
};

export const processMetricValue = (
  value: unknown,
  metricKey: string,
  point: ForecastDataPoint,
): string | number => {
  const editable = isEditableMonth(point.year, point.month);
  if (metricKey === 'forecastProposal') {
    if (!editable && value == null) return '';
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? (editable ? 0 : '') : numeric;
  }
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return value;
  return String(value);
};

export function MetricCell({
  row,
}: {
  row: { original: TableDataRow; getValue: (key: string) => unknown };
}) {
  const { t } = useTranslation('forecast');
  return (
    <div className='flex items-center gap-2'>
      <div
        className='size-1.5 rounded-full'
        style={{ backgroundColor: row.original.color }}
      />
      <span>{row.getValue('metric') as string}</span>
      {row.original.metric === 'Forecast Proposal' && (
        <Tooltip>
          <TooltipTrigger>
            <Info className='mt-0.5 size-3 text-layout-foreground-secondary' />
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('This line is editable')}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export function TimeColumnHeader({ timeColumn }: { timeColumn: string }) {
  return (
    <div className='relative -ml-4 size-14 text-center font-medium text-layout-foreground text-xs'>
      <div className='absolute inset-0 flex items-center justify-center'>
        <span className='origin-center -rotate-45 transform whitespace-nowrap text-xs'>
          {timeColumn}
        </span>
      </div>
    </div>
  );
}

export function createTimeColumnCell(
  data: ForecastDataPoint[],
  sku: string,
  onUpdateForecastProposal: (
    sku: string,
    monthId: string,
    value: number,
  ) => void,
  editable: boolean,
) {
  function TimeColumnCell({
    getValue,
    row,
    column,
  }: CellContext<TableDataRow, unknown>) {
    const value = getValue() as string | number;
    const columnIndex = column.id ? parseInt(column.id.split('-')[1], 10) : -1;
    if (
      row.original.metric === 'Forecast Proposal' &&
      columnIndex >= 0 &&
      data[columnIndex]
    ) {
      const dataPoint = data[columnIndex];
      const editableMonth = isEditableMonth(dataPoint.year, dataPoint.month);
      if (
        typeof value === 'number' &&
        dataPoint.forecastProposal !== undefined &&
        editableMonth
      ) {
        return (
          <ForecastPaginatedTableEditableCell
            editable={editable}
            isHistory={!editableMonth}
            monthId={dataPoint.month_id}
            onUpdate={onUpdateForecastProposal}
            sku={sku}
            value={value}
          />
        );
      }
    }
    return (
      <div>{value === '' ? '-' : Math.round(Number(value) * 100) / 100}</div>
    );
  }
  return TimeColumnCell;
}
